import { useEffect } from 'react';

import BpkBadge, {
  BADGE_TYPES,
} from '@skyscanner/backpack-web/bpk-component-badge/src/BpkBadge';
import { BpkButtonV2 } from '@skyscanner/backpack-web/bpk-component-button';
import BpkCard from '@skyscanner/backpack-web/bpk-component-card';
import BpkPriceTagIcon from '@skyscanner/backpack-web/bpk-component-icon/sm/price-tag';
import { BpkInsetBanner } from '@skyscanner/backpack-web/bpk-component-inset-banner';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';
import { surfaceLowContrastDay } from '@skyscanner/bpk-foundations-web/tokens/base.es6';

/* CugBadge need to be changed a new component by using data from ubff not implement in FE, also need to consider the deals logic
 the new one will be handled in https://skyscanner.atlassian.net/browse/BD-8632, here using the old one just see the layout
 */
import OfferFeatures from '../OfferFeatures';
import PriceInfo from '../PriceInfo';

import type { OnTrackPriceSelected } from '../types';
import type { Configs } from '@shared/types/configs';
import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { PriceBreakdownInfo } from '@shared/types/frontend/price-breakdown';
import type {
  Offer,
  PriceType,
  Partner,
} from '@shared/types/frontend/ubff/hotel-detail-price';
import type { I18nService } from '@shared/types/i18n';

import STYLES from './OfferCard.module.scss';

import {
  getGreatPriceTag,
  hasCheapestForHotelTag,
  hasBookingGeniusTag,
  refactorFeatures,
} from '@hotels-hotel-detail/utils/hotel-offers';
import DealBadge from '@shared/ui/components/DealBadge';
import PartnerInfo from '@shared/ui/components/PartnerInfo';
import { withI18n } from '@shared/ui/contexts/i18n';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import { withConfiguration } from '@shared/utils-frontend/contexts/ConfigurationContext';

const cls = cssModules(STYLES);

type Props = {
  i18n: I18nService;
  offer: Offer;
  isMobile: boolean;
  partner?: Partner;
  onClick: OnTrackPriceSelected;
  costInfo?: PriceBreakdownInfo;
  priceType?: PriceType;
  configs: Configs;
  miniEventTracker: MiniEventTracker;
  hotelName?: string;
};

const OfferCard = ({
  configs: { bookDirectlyWithHotelVar, bookingLoyaltyInfoVar },
  costInfo,
  hotelName,
  i18n: { formatCurrency, translate },
  isMobile,
  miniEventTracker,
  offer,
  onClick,
  partner,
  priceType,
}: Props) => {
  const {
    features,
    officialPartner,
    partner: partnerInOffer,
    price: { price, tags },
    roomName,
    showPartnerLogo,
    url,
  } = offer;

  const dealTag = getGreatPriceTag(tags);
  const offerFeatures = refactorFeatures(features);
  const isLowest = hasCheapestForHotelTag(tags);
  const hasGeniusTag = hasBookingGeniusTag(tags);

  const { logo: logoLink, name: partnerName } = partner ?? partnerInOffer;

  const goToSiteLabel = translate('OTARates_button_goToSite');
  const formattedPrice = formatCurrency(price);

  const showGeniusBanner =
    (bookingLoyaltyInfoVar === 'b' || bookingLoyaltyInfoVar === 'c') &&
    hasGeniusTag;
  const showGeniusBadge = bookingLoyaltyInfoVar === 'c' && hasGeniusTag;
  const showCheapest = !dealTag && !showGeniusBadge && isLowest;

  useEffect(() => {
    if (hasGeniusTag) {
      miniEventTracker.trackExperimentChokepoint('hotels_with_genius_discount');
    }
  }, [hasGeniusTag, miniEventTracker]);

  return (
    <BpkCard
      padded={false}
      data-testid="offer-card"
      className={cls('OfferCard')}
      aria-label={`${goToSiteLabel}, ${partnerName}, ${roomName}, ${formattedPrice}`}
      onClick={onClick}
      href={url}
      target="_blank"
      atomic
    >
      {showGeniusBadge || dealTag ? (
        <div
          className={cls('OfferCard__wrapper', 'OfferCard__newPartnerLogoTag')}
        >
          {showGeniusBadge ? (
            <DealBadge
              icon={null}
              text={translate('loyalty_info_booking_4th')}
            />
          ) : null}
          {dealTag ? (
            <DealBadge
              icon={<BpkPriceTagIcon />}
              text={translate('Dayview_card_wrapper_great_price')}
            />
          ) : null}
        </div>
      ) : null}
      {showCheapest ? (
        <BpkBadge
          type={BADGE_TYPES.normal}
          aria-hidden={false}
          className={cls('OfferCard__cheapest')}
        >
          {translate('our_cheapest_price')}
        </BpkBadge>
      ) : null}
      <div className={cls('OfferCard__wrapper', 'OfferCard__newPartnerLogo')}>
        <PartnerInfo
          logoLink={logoLink}
          partnerName={partnerName}
          showPartnerLogo={showPartnerLogo}
          isOfficialPartner={officialPartner}
          hotelName={hotelName}
          textStyle={
            bookDirectlyWithHotelVar ===
            'official_icon_with_text_and_hotel_name'
              ? TEXT_STYLES.label3
              : TEXT_STYLES.label2
          }
        />
        <div className={cls('OfferCard__price')}>
          <PriceInfo
            offer={offer}
            isMobile={isMobile}
            costInfo={costInfo}
            priceType={priceType}
            onTrackPriceSelected={onClick}
          />
        </div>
      </div>
      <BpkText
        textStyle={TEXT_STYLES.label1}
        tagName="p"
        className={cls('OfferCard__roomName')}
        title={roomName}
      >
        {roomName}
      </BpkText>
      <div className={cls('OfferCard__flexEndWrapper')}>
        {offerFeatures.length > 0 ? (
          <OfferFeatures features={offerFeatures} />
        ) : null}
        <BpkButtonV2 className={cls('OfferCard__btn')}>
          {goToSiteLabel}
        </BpkButtonV2>
      </div>
      {showGeniusBanner && (
        <div
          className={cls('OfferCard__geniusBannerWrapper')}
          data-testid="genius_loyalty_banner"
        >
          <BpkInsetBanner
            backgroundColor={surfaceLowContrastDay}
            subheadline={translate('loyalty_info_booking_3rd')}
          />
        </div>
      )}
    </BpkCard>
  );
};

export default withMiniEventTracker(withConfiguration(withI18n(OfferCard)));
